import { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';

/* ── Predefined static lists ── */
const ISSUERS = [
  'Allianz-Tiriac', 'Omniasig', 'Generali', 'Grawe', 'Uniqa',
  'Asirom', 'Groupama', 'RAR', 'ING Asigurari', 'Signal Iduna',
];
const LEASING_COMPANIES = [
  'BCR Leasing', 'BRD Leasing', 'Raiffeisen Leasing', 'UniCredit Leasing',
  'Toyota Financial Services', 'ING Leasing', 'TBI Leasing', 'Impuls Leasing',
  'Porsche Finance Group', 'VB Leasing',
];
const BRANDS = [
  'BMW', 'Mercedes-Benz', 'Volkswagen', 'Dacia', 'Renault', 'Ford', 'Opel',
  'Audi', 'Toyota', 'Hyundai', 'Kia', 'Skoda', 'Volvo', 'Fiat', 'Peugeot',
  'Citroën', 'Seat', 'Nissan', 'Mitsubishi', 'Honda', 'Mazda', 'Suzuki',
];
const CITIES = [
  'Bucharest', 'Cluj-Napoca', 'Timisoara', 'Iasi', 'Constanta', 'Brasov',
  'Craiova', 'Galati', 'Ploiesti', 'Oradea', 'Sibiu', 'Arad', 'Pitesti',
  'Baia Mare', 'Bacau', 'Suceava', 'Targu Mures', 'Deva', 'Ramnicu Valcea',
];
const LICENSE_CATS = ['B', 'A', 'A1', 'A2', 'AM', 'C', 'C1', 'CE', 'C1E', 'D', 'D1', 'DE', 'BE'];
const COLORS = ['White', 'Black', 'Grey', 'Silver', 'Blue', 'Red', 'Green', 'Yellow', 'Orange', 'Brown'];

/* ── Module-level singleton so data loads once for the whole app ── */
let _cache = null;
const _subs = new Set();

function _notify() { _subs.forEach(fn => fn()); }

function _dedup(arr) {
  return [...new Set((arr || []).filter(Boolean).map(s => String(s).trim()).filter(Boolean))];
}

async function _load() {
  try {
    const [vR, dR, cR, sR, eR] = await Promise.all([
      apiEndpoints.vehicles.list().catch(() => ({ data: [] })),
      apiEndpoints.drivers.list().catch(()   => ({ data: [] })),
      apiEndpoints.companies.list().catch(() => ({ data: [] })),
      apiEndpoints.suppliers.list().catch(() => ({ data: [] })),
      apiEndpoints.events.list().catch(()    => ({ data: [] })),
    ]);
    const vehicles  = vR.data || [];
    const drivers   = dR.data || [];
    const companies = cR.data || [];
    const suppliers = sR.data || [];
    const events    = eR.data || [];

    _cache = {
      _vehicles: vehicles,
      _drivers:  drivers,
      plates:            _dedup(vehicles.map(v => v.plate)),
      vins:              _dedup(vehicles.map(v => v.vin)),
      vehicleNames:      _dedup(vehicles.map(v => [v.brand, v.model].filter(Boolean).join(' '))),
      allModels:         _dedup(vehicles.map(v => v.model)),
      brands:            _dedup([...BRANDS, ...vehicles.map(v => v.brand)]),
      companyNames:      _dedup(companies.map(c => c.name || c.label)),
      driverNames:       _dedup(drivers.map(d => d.name)),
      roles:             _dedup(drivers.map(d => d.role)),
      cities:            _dedup([...CITIES, ...suppliers.map(s => s.city)]),
      supplierNames:     _dedup(suppliers.map(s => s.name)),
      eventNames:        _dedup(events.map(e => e.evt)),
      issuers:           ISSUERS,
      leasingCompanies:  LEASING_COMPANIES,
      licenseCategories: LICENSE_CATS,
      colors:            COLORS,
    };
    _notify();
  } catch (err) {
    console.error('[useFormData] load failed:', err);
  }
}

/* ── Hook ── */
export function useFormData() {
  const [, tick] = useState(0);

  useEffect(() => {
    const update = () => tick(n => n + 1);
    _subs.add(update);
    if (!_cache) _load();
    return () => _subs.delete(update);
  }, []);

  /** Look up a vehicle by its plate and return brand+model and company. */
  const getVehicleByPlate = (plate) =>
    _cache?._vehicles?.find(v => v.plate === plate) ?? null;

  /** Get a driver object by name. */
  const getDriverByName = (name) =>
    _cache?._drivers?.find(d => d.name === name) ?? null;

  /** Models that belong to a specific brand. */
  const modelsForBrand = (brand) => {
    if (!brand || !_cache) return [];
    return _dedup(
      _cache._vehicles
        .filter(v => (v.brand || '').toLowerCase() === brand.toLowerCase())
        .map(v => v.model),
    );
  };

  return {
    plates:            _cache?.plates            ?? [],
    vins:              _cache?.vins              ?? [],
    vehicleNames:      _cache?.vehicleNames      ?? [],
    allModels:         _cache?.allModels         ?? [],
    brands:            _cache?.brands            ?? [],
    companyNames:      _cache?.companyNames      ?? [],
    driverNames:       _cache?.driverNames       ?? [],
    roles:             _cache?.roles             ?? [],
    cities:            _cache?.cities            ?? [],
    supplierNames:     _cache?.supplierNames     ?? [],
    eventNames:        _cache?.eventNames        ?? [],
    issuers:           _cache?.issuers           ?? [],
    leasingCompanies:  _cache?.leasingCompanies  ?? [],
    licenseCategories: _cache?.licenseCategories ?? [],
    colors:            _cache?.colors            ?? [],
    getVehicleByPlate,
    getDriverByName,
    modelsForBrand,
  };
}
